<!--
  Send feedback about Changeling to its operators: a note, and, only when
  ticked, a screenshot of the page (annotated, code blurred or not), the
  app's recent log lines, and an element picked on the page. The preview
  at the bottom is the report exactly as it will be sent.
-->
<script lang="ts">
  import { feedbackUi } from '../feedback/feedback.svelte';
  import { appLog, type LogLine } from '../feedback/logs';
  import { buildReport, clientVersion, LIMITS, type Draft, type PickedElement } from '../feedback/report';
  import { canCaptureDisplay, captureDisplay, captureDom, encode, NotThisTab, steppingAside, type Capture } from '../feedback/capture';
  import { fileFeedback } from '../api/feedback';
  import Annotator from './Annotator.svelte';
  import ElementPicker from './ElementPicker.svelte';

  let dialog: HTMLDialogElement | undefined = $state();
  let note = $state('');
  let shotOn = $state(false);
  let shotStatus = $state<'idle' | 'capturing' | 'ready' | 'failed'>('idle');
  let shotError = $state('');
  let capture = $state.raw<Capture | null>(null);
  let blurCode = $state(true);
  let encoded = $state<{ base64: string; type: string; bytes: number } | null>(null);
  let encoding: Promise<void> | null = null;
  let logsOn = $state(false);
  let logLines = $state<LogLine[]>([]);
  let element = $state<PickedElement | null>(null);
  let picking = $state(false);
  /** The dialog is hidden while the browser captures the tab. */
  let aside = $state(false);
  let sending = $state(false);
  let sent = $state(false);
  let error = $state('');

  const entry = typeof document === 'undefined' ? null : (document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null);
  const env = {
    clientVersion: clientVersion(entry, import.meta.env.VITE_ENGINE_BUILD ?? 'none'),
    browser: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  };
  let route = $state('');

  const draft: Draft = $derived({
    note,
    route,
    screenshot: { attached: shotOn, png: encoded?.base64 ?? null },
    logs: { attached: logsOn, lines: logLines },
    element,
  });
  const body = $derived(buildReport(draft, env));
  const noteBytes = $derived(new TextEncoder().encode(note.trim()).length);
  const canSend = $derived(noteBytes > 0 && noteBytes <= LIMITS.note && !sending && !(shotOn && shotStatus === 'capturing'));

  $effect(() => {
    if (!dialog) return;
    if (aside) return;
    if (feedbackUi.open && !picking && !dialog.open) {
      route = location.pathname + location.search + location.hash;
      dialog.showModal();
    } else if ((!feedbackUi.open || picking) && dialog.open) dialog.close();
  });

  function close() {
    feedbackUi.open = false;
    if (sent) reset();
  }

  function reset() {
    note = '';
    shotOn = false;
    shotStatus = 'idle';
    capture = null;
    encoded = null;
    blurCode = true;
    logsOn = false;
    logLines = [];
    element = null;
    sent = false;
    error = '';
  }


  async function takeScreenshot(how: 'dom' | 'display') {
    shotStatus = 'capturing';
    shotError = '';
    encoded = null;
    try {
      if (how === 'display') {
        // The browser captures what is on screen: this dialog is hidden first.
        aside = true;
        try {
          capture = await steppingAside(dialog!, () => captureDisplay());
        } finally {
          aside = false;
        }
      } else {
        capture = await captureDom();
      }
      shotStatus = 'ready';
    } catch (e) {
      capture = null;
      if (e instanceof NotThisTab) {
        // Another tab, window or screen: discarded; the page is drawn from its DOM instead.
        try {
          capture = await captureDom();
          shotStatus = 'ready';
          shotError = e.message;
          return;
        } catch {
          /* reported below */
        }
      }
      shotStatus = 'failed';
      shotError =
        how === 'dom'
          ? 'This browser would not draw the page.'
          : e instanceof DOMException && e.name === 'NotAllowedError'
            ? 'Capturing the tab was not allowed.'
            : `The tab could not be captured (${e instanceof Error ? e.message : String(e)}).`;
    }
  }

  function toggleShot() {
    if (shotOn && !capture && shotStatus !== 'capturing') void takeScreenshot('dom');
  }

  function toggleLogs() {
    if (logsOn) logLines = appLog.lines();
  }

  let encodeTimer: ReturnType<typeof setTimeout> | undefined;
  function composed(canvas: HTMLCanvasElement) {
    clearTimeout(encodeTimer);
    encodeTimer = setTimeout(() => {
      const run = encode(canvas).then((e) => {
        encoded = e;
      });
      encoding = run;
      void run.finally(() => {
        if (encoding === run) encoding = null;
      });
    }, 250);
  }

  function pick() {
    picking = true;
  }

  function picked(p: PickedElement) {
    element = p;
    picking = false;
  }

  async function send() {
    if (!canSend) return;
    sending = true;
    error = '';
    try {
      // The preview shows the screenshot as last encoded; wait for one in progress.
      if (shotOn && capture) {
        clearTimeout(encodeTimer);
        if (encoding) await encoding;
        if (!encoded) throw new Error('The screenshot is still being prepared; try again.');
      }
      await fileFeedback(buildReport(draft, env));
      sent = true;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      sending = false;
    }
  }

  const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
</script>

<dialog
  class="sheet feedback"
  data-feedback-ignore
  bind:this={dialog}
  onclose={() => {
    if (!picking && !aside) close();
  }}
  aria-labelledby="feedback-title"
>
  <div class="sheet-head">
    <h2 id="feedback-title">Send feedback</h2>
    <button class="btn small" onclick={close}>Close · Esc</button>
  </div>
  {#if sent}
    <p class="done" role="status">Thanks. Your feedback reached Changeling’s operators.</p>
    <div class="sheet-actions"><span></span><button class="btn primary" onclick={close}>Done</button></div>
  {:else}
    <p>
      Tell Changeling’s operators what went wrong or what you would like. Only your note and the page’s address, the app’s
      version and your browser are sent; tick anything else you want to attach. Operators may copy your note (never your
      name, screenshot or logs) into a GitHub issue.
    </p>
    <label class="field">
      <span>What happened?</span>
      <textarea bind:value={note} rows="4" placeholder="What you did, what you expected, what you saw"></textarea>
      {#if noteBytes > LIMITS.note}<small class="negative">At most {LIMITS.note.toLocaleString()} characters.</small>{/if}
    </label>

    <fieldset class="attach">
      <legend>Attach</legend>
      <label class="opt"><input type="checkbox" bind:checked={shotOn} onchange={toggleShot} /> A screenshot of this page</label>
      {#if shotOn}
        <div class="sub">
          {#if shotStatus === 'capturing'}
            <p class="muted" role="status">Capturing the page…</p>
          {:else if shotStatus === 'failed'}
            <p class="negative" role="alert">{shotError}</p>
            <div class="row">
              {#if canCaptureDisplay()}<button class="btn small" onclick={() => takeScreenshot('display')}>Capture the tab with the browser</button>{/if}
              <button class="btn small" onclick={() => takeScreenshot('dom')}>Try again</button>
            </div>
          {:else if capture}
            {#if shotError}<p class="negative small" role="status">{shotError}</p>{/if}
            <Annotator {capture} bind:blurCode onchange={composed} />
            <div class="row muted small">
              <span>
                {#if capture.omitted.frames || capture.omitted.images}Left out: {[
                    capture.omitted.frames ? `${capture.omitted.frames} embedded page${capture.omitted.frames === 1 ? '' : 's'}` : '',
                    capture.omitted.images ? `${capture.omitted.images} image${capture.omitted.images === 1 ? '' : 's'} from other sites` : '',
                  ]
                    .filter(Boolean)
                    .join(' and ')}.{:else}The visible part of the page.{/if}
              </span>
              <button class="btn small" onclick={() => takeScreenshot('dom')}>Retake</button>
              {#if capture.via === 'dom' && canCaptureDisplay()}<button class="btn small" onclick={() => takeScreenshot('display')}>Capture with the browser</button>{/if}
            </div>
          {/if}
        </div>
      {/if}

      <label class="opt"
        ><input type="checkbox" bind:checked={logsOn} onchange={toggleLogs} /> The app’s recent log lines{#if logsOn}&nbsp;({logLines.length}){/if}</label
      >
      {#if logsOn}
        <p class="sub muted small">
          Warnings, errors and failed requests with their IDs, one line each, with tokens, cookies and anything spanning lines (code,
          comment text) left out. All of them are listed below.
        </p>
      {/if}

      <div class="opt element">
        {#if element}
          <span>Element: <code>{element.selector}</code></span>
          <button class="btn small" onclick={pick}>Pick another</button>
          <button class="btn small" onclick={() => (element = null)}>Remove</button>
        {:else}
          <button class="btn small" onclick={pick}>Pick an element on the page</button>
          <span class="muted small">to point at what you mean</span>
        {/if}
      </div>
    </fieldset>

    <section class="preview" aria-labelledby="feedback-preview">
      <h3 id="feedback-preview">What will be sent</h3>
      <dl>
        <dt>Note</dt>
        <dd>{#if body.note}<span class="pre">{body.note}</span>{:else}<span class="muted">(nothing yet)</span>{/if}</dd>
        <dt>Page</dt>
        <dd><code>{body.route}</code></dd>
        <dt>Version</dt>
        <dd><code>{body.client_version}</code></dd>
        <dt>Browser</dt>
        <dd class="small">{body.browser}</dd>
        <dt>Screenshot</dt>
        <dd>
          {#if body.screenshot && encoded}
            <img class="thumb" src="data:{encoded.type};base64,{encoded.base64}" alt="The screenshot as it will be sent" />
            <span class="muted small">{encoded.type === 'image/png' ? 'PNG' : 'JPEG'}, {kb(encoded.bytes)}{blurCode && capture?.codeAreas.length ? ', code blurred' : ''}</span>
          {:else if shotOn && capture}<span class="muted">Preparing…</span>
          {:else}<span class="muted">Not attached</span>{/if}
        </dd>
        <dt>Logs</dt>
        <dd>
          {#if body.logs}
            <ol class="loglines">
              {#each body.logs as l, i (i)}<li><span class="lvl {l.level}">{l.level}</span> {l.text}</li>{/each}
            </ol>
          {:else if logsOn}<span class="muted">Nothing logged yet</span>{:else}<span class="muted">Not attached</span>{/if}
        </dd>
        <dt>Element</dt>
        <dd>
          {#if body.element}<code>{body.element.selector}</code>{#if body.element.text}<div class="small">“{body.element.text}”</div>{/if}
          {:else}<span class="muted">None</span>{/if}
        </dd>
      </dl>
    </section>

    {#if error}<p class="negative" role="alert">{error}</p>{/if}
    <div class="sheet-actions">
      <span class="muted small">Kept for 90 days, then deleted.</span>
      <span class="row">
        <button class="btn" onclick={close}>Cancel</button>
        <button class="btn primary" onclick={send} disabled={!canSend}>{sending ? 'Sending…' : 'Send feedback'}</button>
      </span>
    </div>
  {/if}
</dialog>

{#if picking && feedbackUi.open && shotStatus !== 'capturing'}
  <ElementPicker onpick={picked} oncancel={() => (picking = false)} />
{/if}

<style>
  dialog.feedback {
    max-width: 760px;
  }
  .field {
    display: grid;
    gap: 6px;
    margin-top: 14px;
    font-weight: 600;
    font-size: 14px;
  }
  .field textarea {
    font-weight: 400;
  }
  .attach {
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 10px 14px 14px;
    margin: 14px 0 0;
    display: grid;
    gap: 10px;
  }
  .attach legend {
    font-size: 12px;
    color: var(--muted);
    padding: 0 6px;
  }
  .opt {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    flex-wrap: wrap;
  }
  .opt input {
    width: 16px;
    height: 16px;
    margin: 0;
  }
  .sub {
    margin: 0 0 0 24px;
    display: grid;
    gap: 8px;
  }
  dialog.sheet .sub p,
  dialog.sheet p.sub {
    margin-top: 0;
  }
  .row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .small {
    font-size: 12px;
  }
  .element code {
    overflow-wrap: anywhere;
  }
  .preview {
    margin-top: 16px;
    border-top: 1px solid var(--line);
    padding-top: 12px;
  }
  .preview h3 {
    font-size: 14px;
  }
  .preview dl {
    display: grid;
    grid-template-columns: 90px 1fr;
    gap: 6px 12px;
    margin: 8px 0 0;
    font-size: 13px;
  }
  .preview dt {
    color: var(--muted);
  }
  .preview dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .pre {
    white-space: pre-wrap;
  }
  .thumb {
    display: block;
    max-width: 100%;
    max-height: 180px;
    border: 1px solid var(--line);
    border-radius: 6px;
    margin-bottom: 4px;
  }
  .loglines {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 160px;
    overflow: auto;
    font: 11px/1.5 ui-monospace, SFMono-Regular, Consolas, monospace;
    background: var(--code);
    border-radius: 6px;
    padding: 6px 8px;
  }
  .lvl {
    color: var(--muted);
  }
  .lvl.error,
  .lvl.request {
    color: var(--bad);
  }
  .lvl.warn {
    color: var(--warn);
  }
  .done {
    font-size: 15px !important;
    color: var(--good) !important;
  }
  @media (max-width: 500px) {
    .preview dl {
      grid-template-columns: 1fr;
    }
    .preview dt {
      margin-top: 6px;
    }
    .sub {
      margin-left: 0;
    }
  }
</style>
